import { Loader, Select } from "@/components";
import { Badge, Button, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useGetOwnershipAssociations } from "@/services";
import { AlertCircle, Building2, ChevronDown, ChevronLeft, ChevronRight, Home, Mail, Phone, Plus, Search, X } from "lucide-react";
import moment from "moment";
import { Fragment, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "@/routes/utils";
import usePropertyOwnership, { OccupancyFilter, StatusFilter } from "./usePropertyOwnership";

const fmt = (value?: string) => (value ? moment(value).format("MM/DD/YYYY") : "—");

const StatusBadge = ({ active, closedLabel = "Closed" }: { active: boolean; closedLabel?: string }) => (
  <Badge
    variant="outline"
    className={cn(
      active ? "border-green-200 bg-green-50 text-green-700" : "border-gray-200 bg-gray-100 text-gray-600",
      "rounded-md px-2 py-0.5"
    )}
  >
    {active ? "Active" : closedLabel}
  </Badge>
);

function Segmented<T extends string>({ value, onChange, options }: {
  value: T; onChange: (v: T) => void; options: { label: string; value: T }[];
}) {
  return (
    <div className="flex items-center gap-1 bg-white border rounded-md p-1 h-[40px]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "px-3 h-full rounded text-sm whitespace-nowrap",
            value === o.value ? "bg-blue-50 text-primary font-medium" : "text-gray-600 hover:bg-gray-50"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const SummaryCard = ({ label, icon, children }: { label: string; icon: ReactNode; children: ReactNode }) => (
  <div className="bg-white rounded-xl shadow-sm p-5 flex items-start justify-between gap-4">
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">{label}</p>
      <div className="flex items-baseline gap-2 flex-wrap text-sm text-gray-600">{children}</div>
    </div>
    {icon}
  </div>
);

/** Expanded row: people on one ownership, loaded when the row is opened. */
const OwnershipPeople = ({ communityId, ownershipId, colSpan, onOpenOwner }: {
  communityId: string; ownershipId: number; colSpan: number; onOpenOwner: (userId: number) => void;
}) => {
  const { data, isLoading } = useGetOwnershipAssociations({ communityId, ownershipId: String(ownershipId) });
  const people = data?.data || [];
  return (
    <TableRow className="bg-blue-50/40 hover:bg-blue-50/40">
      <TableCell colSpan={colSpan} className="px-6 py-3">
        {isLoading ? <p className="text-sm text-gray-500">Loading…</p> : people.length === 0 ? (
          <p className="text-sm text-gray-500">No people linked to this ownership.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {["Owner / User ID", "Person", "Relationship", "Phone", "Email ID", "Effective Period", "Closing Date", "Status"]
                  .map((h) => <TableHead key={h} className="font-semibold text-gray-800">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {people.map((p: any) => (
                <TableRow key={p.propertyUserAssociationId} className="hover:bg-transparent">
                  <TableCell>
                    <button className="text-primary font-semibold hover:underline" onClick={() => onOpenOwner(p.userId)}>
                      {p.userId}
                    </button>
                  </TableCell>
                  <TableCell>{[p.firstName, p.lastName].filter(Boolean).join(" ") || "—"}</TableCell>
                  <TableCell>{p.associationType || "—"}</TableCell>
                  <TableCell>{p.phoneNumber || "—"}</TableCell>
                  <TableCell>{p.email || "—"}</TableCell>
                  <TableCell>{fmt(p.startDate)}</TableCell>
                  <TableCell>{p.endDate ? fmt(p.endDate) : "Open"}</TableCell>
                  <TableCell><StatusBadge active={p.active} closedLabel="Inactive" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableCell>
    </TableRow>
  );
};

const HEADERS = ["Property Ownership ID", "Property", "Primary Contact", "Primary Phone Number", "Primary Email ID", "Effective From", "Status"];

const PropertyOwnership = () => {
  const {
    communityList, isCommunityLoading, communityId, communityKey, onCommunityChange,
    occupancy, setOccupancy, status, setStatus, searchInput, setSearchInput, applySearch,
    rows, total, isGridLoading, isGridFetching, pageNumber, setPageNumber, pageSize, setPageSize,
    expanded, toggleExpanded, summary, openOwnership, openOwner, openProperty, ownershipFilter, clearOwnershipFilter,
  } = usePropertyOwnership();
  const navigate = useNavigate();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : pageNumber * pageSize + 1;
  const to = Math.min(total, (pageNumber + 1) * pageSize);

  return (
    <div className="p-4 md:p-6 flex flex-col gap-5">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg md:text-xl font-semibold">Ownership</h2>
          <Button onClick={() => navigate(routes.newOwnership)} disabled={!communityId || communityId == "all"}>
            <Plus className="w-4 h-4" /> New Ownership
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full sm:w-60 shrink-0">
            <Select
              key={communityKey}
              options={communityList?.data?.map((c: any) => ({ value: c?.communityId?.toString(), label: c?.name }))}
              value={communityId?.toString()}
              onChange={onCommunityChange}
              className="h-[40px] w-full"
              disabled={isCommunityLoading}
            />
          </div>
          <Segmented<OccupancyFilter> value={occupancy} onChange={setOccupancy} options={[
            { label: "All", value: "ALL" },
            { label: "Owner Occupied", value: "OWNER_OCCUPIED" },
            { label: "Tenant Occupied", value: "TENANT_OCCUPIED" },
          ]} />
          <Segmented<StatusFilter> value={status} onChange={setStatus} options={[
            { label: "Active", value: "ACTIVE" },
            { label: "Closed", value: "CLOSED" },
            { label: "All", value: "ALL" },
          ]} />
          <div className="flex-1 min-w-[220px]">
            <Input
              type="text"
              placeholder="Search PO ID, property, contact, phone or email — press Enter"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
              className="w-full bg-white h-[40px]"
              startIcon={<Search className="w-4 h-4" />}
            />
          </div>
          {ownershipFilter ? (
            <Badge variant="outline" className="h-[40px] rounded-md px-3 gap-2 bg-blue-50 border-blue-200 text-primary text-sm">
              Showing PO-{ownershipFilter}
              <button type="button" onClick={clearOwnershipFilter} aria-label="Clear ownership filter" className="hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </Badge>
          ) : <></>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard label="Total Title Coverage" icon={<Building2 className="w-6 h-6 text-primary" />}>
          <span className="text-2xl font-semibold text-gray-900">{summary?.activeOwnerships ?? "—"}</span>
          active ownerships / {summary?.totalProperties ?? "—"} properties
        </SummaryCard>
        <SummaryCard label="Ownership vs Rental" icon={<Home className="w-6 h-6 text-purple-600" />}>
          <span className="text-2xl font-semibold text-gray-900">{summary?.ownerOccupied ?? "—"}</span> owner occupied
          <span className="text-2xl font-semibold text-purple-600 ml-2">{summary?.tenantOccupied ?? "—"}</span> tenant occupied
        </SummaryCard>
        <SummaryCard label="Coverage Gaps" icon={<AlertCircle className="w-6 h-6 text-orange-500" />}>
          <span className="text-2xl font-semibold text-orange-500">{summary?.propertiesWithoutActiveTitle ?? "—"}</span>
          properties without active title
        </SummaryCard>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-2 md:p-4">
        {isGridLoading ? <Loader /> : (
          <>
            <div className={cn("overflow-x-auto", isGridFetching && "opacity-60")}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[44px]" />
                    {HEADERS.map((h) => <TableHead key={h} className="font-semibold text-primary whitespace-nowrap">{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={HEADERS.length + 1} className="text-center text-gray-500 h-20">No ownerships found.</TableCell></TableRow>
                  ) : rows.map((row: any) => (
                    <Fragment key={row.ownershipId}>
                      <TableRow>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => toggleExpanded(row.ownershipId)}
                            className="flex items-center justify-center border border-primary text-primary rounded-full p-0.5 hover:bg-primary/10"
                            aria-label={expanded[row.ownershipId] ? "Collapse" : "Expand"}
                          >
                            {expanded[row.ownershipId] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </TableCell>
                        <TableCell>
                          <button className="text-primary font-semibold hover:underline" onClick={() => openOwnership(row.ownershipId)}>
                            {row.ownershipCode}
                          </button>
                        </TableCell>
                        <TableCell>
                          {row.propertyId ? (
                            <button className="text-primary hover:underline text-left" onClick={() => openProperty(row.propertyId, row.propertyName)}>
                              {row.propertyName || `Property ${row.propertyId}`}
                            </button>
                          ) : "—"}
                        </TableCell>
                        <TableCell>{row.primaryContactName || "—"}</TableCell>
                        <TableCell>
                          {row.primaryPhone ? <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" />{row.primaryPhone}</span> : "—"}
                        </TableCell>
                        <TableCell>
                          {row.primaryEmail ? <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gray-400" />{row.primaryEmail}</span> : "—"}
                        </TableCell>
                        <TableCell>{fmt(row.startDate)}</TableCell>
                        <TableCell><StatusBadge active={row.active} /></TableCell>
                      </TableRow>
                      {expanded[row.ownershipId] ? (
                        <OwnershipPeople communityId={communityId} ownershipId={row.ownershipId}
                          colSpan={HEADERS.length + 1} onOpenOwner={openOwner} />
                      ) : <></>}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-end gap-4 pt-3 text-sm text-gray-600">
              <span>Rows per page:</span>
              <select className="bg-transparent" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>{from} - {to} of {total}</span>
              <button disabled={pageNumber === 0} onClick={() => setPageNumber(pageNumber - 1)} className="disabled:opacity-30" aria-label="Previous page">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button disabled={pageNumber + 1 >= totalPages} onClick={() => setPageNumber(pageNumber + 1)} className="disabled:opacity-30" aria-label="Next page">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PropertyOwnership;
